import {useMemo, useState} from "react";
import {
    CZECH_REGION_CENTROIDS,
    CZECH_REGION_PATHS,
    MAP_KEY_TO_LABEL,
    REGION_API_TO_MAP_KEY
} from "./czechRegionsPaths";
import "../css/dashboard-projects.css";

function resolveMapKey(region) {
    if (!region) {
        return null;
    }
    return REGION_API_TO_MAP_KEY[region.shortName]
        || REGION_API_TO_MAP_KEY[region.name]
        || null;
}

function getFillColor(count, maxCount) {
    if (!count || count <= 0) {
        return "#e8eef5";
    }
    if (!maxCount || maxCount <= 0) {
        return "#9bb8d9";
    }
    const intensity = Math.min(1, count / maxCount);
    // Light blue → gov primary blue
    const r = Math.round(232 - intensity * (232 - 30));
    const g = Math.round(238 - intensity * (238 - 90));
    const b = Math.round(245 - intensity * (245 - 168));
    return `rgb(${r}, ${g}, ${b})`;
}

// Cropped to actual CZ outline (paths start ~y=440; full 0..800 left a huge empty gap)
const MAP_VIEW_BOX = "-8 436 628 368";

export default function CzechRepublicProjectsMap({regionCounts = [], title = "Počet projektů dle krajů"}) {
    const [hoveredKey, setHoveredKey] = useState(null);
    const [tooltip, setTooltip] = useState(null);

    const countsByMapKey = useMemo(() => {
        const map = {};
        Object.keys(CZECH_REGION_PATHS).forEach((key) => {
            map[key] = 0;
        });
        regionCounts.forEach((item) => {
            const key = resolveMapKey(item.region);
            if (key) {
                map[key] = item.totalCount || 0;
            }
        });
        return map;
    }, [regionCounts]);

    const maxCount = useMemo(() => {
        return Math.max(0, ...Object.values(countsByMapKey));
    }, [countsByMapKey]);

    const handleMouseEnter = (key, event) => {
        setHoveredKey(key);
        setTooltip({
            x: event.clientX,
            y: event.clientY,
            label: MAP_KEY_TO_LABEL[key] || key,
            count: countsByMapKey[key] || 0
        });
    };

    const handleMouseMove = (event) => {
        setTooltip((prev) => prev ? {...prev, x: event.clientX, y: event.clientY} : prev);
    };

    const handleMouseLeave = () => {
        setHoveredKey(null);
        setTooltip(null);
    };

    return (
        <div className="czech-projects-map">
            <h3 className="czech-projects-map__title">{title}</h3>
            <div className="czech-projects-map__canvas">
                <svg
                    viewBox={MAP_VIEW_BOX}
                    role="img"
                    aria-label={title}
                    className="czech-projects-map__svg"
                >
                    {Object.entries(CZECH_REGION_PATHS).map(([key, path]) => {
                        const count = countsByMapKey[key] || 0;
                        const isHovered = hoveredKey === key;
                        return (
                            <path
                                key={key}
                                d={path}
                                fill={isHovered ? "#2362a2" : getFillColor(count, maxCount)}
                                stroke="#4a5568"
                                strokeWidth={isHovered ? 1.5 : 0.8}
                                className="czech-projects-map__region"
                                onMouseEnter={(event) => handleMouseEnter(key, event)}
                                onMouseMove={handleMouseMove}
                                onMouseLeave={handleMouseLeave}
                            />
                        );
                    })}
                    {Object.entries(CZECH_REGION_CENTROIDS).map(([key, point]) => {
                        const count = countsByMapKey[key] || 0;
                        return (
                            <text
                                key={`label-${key}`}
                                x={point.x}
                                y={point.y}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                className="czech-projects-map__count"
                                pointerEvents="none"
                            >
                                {count}
                            </text>
                        );
                    })}
                </svg>
                {tooltip && (
                    <div
                        className="czech-projects-map__tooltip"
                        style={{top: tooltip.y + 16, left: tooltip.x + 16}}
                    >
                        <strong>{tooltip.label}</strong>
                        <span>Počet projektů: {tooltip.count}</span>
                    </div>
                )}
            </div>
        </div>
    );
}
